import { useCallback, useEffect, useState } from 'react';
import { apiCall } from '../../utils/fetchAPI';
import { translateApiMessage } from '../../utils/apiMessages';

/**
 * Appointments, from whichever side is asking.
 *
 * `/me/bookings` is the resident's own; `/me/schedule` is the professional's
 * diary. Same shape, same actions, so one hook serves both screens.
 */
export function useMyBookings(path = '/me/bookings') {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const response = await apiCall(`${path}?limit=100`, { method: 'GET' });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(translateApiMessage(data.error) || 'Vos rendez-vous n’ont pas pu être chargés.');
      }

      setBookings(data.data || []);
      setError('');
    } catch (err) {
      setBookings([]);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * confirm / decline / cancel / complete. Returns an error message, or an
   * empty string when it went through.
   */
  const act = useCallback(
    async (uuid, action, body = null) => {
      const response = await apiCall(`/bookings/${uuid}/${action}`, {
        method: 'POST',
        ...(null === body ? {} : { body: JSON.stringify(body) }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));

        return translateApiMessage(data.error) || 'L’action n’a pas pu être enregistrée.';
      }

      await load();

      return '';
    },
    [load]
  );

  return { bookings, loading, error, reload: load, act };
}
